/**
 * Trainer initialization script
 * Handles dynamic verb loading and app initialization with full TypeScript support
 */

import type { VerbData } from "@/types";

declare global {
  interface Window {
    verbData: VerbData;
    app: any;
  }
}

/**
 * Get verb name from URL parameters
 */
function getVerbFromURL(): string {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("verb") || "werden";
}

/**
 * Wait for VerbTrainerApp to be available on window object
 */
function waitForVerbTrainerApp(): Promise<void> {
  return new Promise((resolve) => {
    if (window.VerbTrainerApp) {
      resolve();
      return;
    }

    const checkInterval = setInterval(() => {
      if (window.VerbTrainerApp) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 10);

    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      resolve();
    }, 5000);
  });
}

/**
 * Dynamically import verb data based on verb name
 */
async function loadVerbData(verbName: string): Promise<void> {
  try {
    switch (verbName) {
      case "werden":
        await import("./data/werden");
        break;
      case "haben":
        await import("./data/haben");
        break;
      case "sein":
        await import("./data/sein");
        break;
      case "können":
        await import("./data/können");
        break;
      default:
        console.warn(`Unknown verb: ${verbName}, falling back to werden`);
        await import("./data/werden");
        break;
    }
  } catch (error) {
    console.error(`Failed to load verb data for ${verbName}:`, error);
    // Fallback to werden
    if (verbName !== "werden") {
      await import("./data/werden");
    }
  }
}

/**
 * Initialize the trainer application
 */
async function initializeTrainer(): Promise<void> {
  const verbName = getVerbFromURL();

  try {
    // Wait for VerbTrainerApp to be available
    await waitForVerbTrainerApp();

    // Load the appropriate verb data dynamically
    await loadVerbData(verbName);

    // Verify verb data is loaded
    if (!window.verbData) {
      throw new Error("Verb data not loaded properly");
    }

    // Initialize the app
    if (window.VerbTrainerApp) {
      window.app = new window.VerbTrainerApp(verbName);

      // Add debug methods to window for testing (can be removed in production)
      (window as any).debugChallengeLevels = () => {
        if (window.app && window.app.questionEngine) {
          window.app.questionEngine.debugChallengeLevels();
        }
      };

      (window as any).checkFormStats = () => {
        if (window.app && window.app.statsTracker) {
          console.log("📊 Current Form Stats Sample:");
          const allForms = window.verbData.getAllForms("all");
          const sampleForms = allForms.slice(0, 10);

          sampleForms.forEach((form) => {
            const stats = window.app.statsTracker.getFormStats(
              form.tense,
              form.pronoun
            );
            console.log(`${form.tense} ${form.pronoun}:`, stats);
          });
        }
      };

      console.log(`✅ German Verb Trainer initialized for "${verbName}"`);
    } else {
      throw new Error("VerbTrainerApp not found on window object");
    }
  } catch (error) {
    console.error("Failed to initialize trainer:", error);

    // Final fallback attempt
    try {
      await loadVerbData("werden");
      if (window.VerbTrainerApp && window.verbData) {
        window.app = new window.VerbTrainerApp("werden");
        console.log('⚠️ Trainer initialized with fallback verb "werden"');
      } else {
        console.error("❌ Complete initialization failure");
      }
    } catch (fallbackError) {
      console.error("❌ Fallback initialization failed:", fallbackError);
    }
  }
}

// Wait for DOM to be ready and then initialize
document.addEventListener("DOMContentLoaded", initializeTrainer);
